import { motion } from 'framer-motion';

// FadeIn — shared scroll-reveal wrapper. Animates child content with a fade
// + slide-in when scrolled into view. Replaces the three duplicated local
// `Reveal` components that lived in HeroSection / ZeroSection / TrustBridge.
//
// Props:
//   children   - content to animate
//   delay      - stagger delay in seconds (e.g., 0.08 = 80ms, matches the
//                old `data-delay="1"` convention)
//   duration   - animation duration in seconds (default 0.6)
//   direction  - 'up' | 'down' | 'left' | 'right' (default 'up')
//   once       - if true, only animates the first time it enters view (default true)
//   amount     - viewport intersection threshold 0..1 (default 0.2)
//   as         - underlying motion tag (default 'div')
//   className  - className passthrough
//   ...rest    - any other props (id, onClick, role, etc.) forwarded to the
//                motion element. Forms get id + onSubmit through this.
//
// `once: true` is the default because repeated re-animating on each scroll
// pass is visually noisy. Pass `once={false}` if you want re-trigger behavior.

const DIRECTION_OFFSETS = {
  up: { y: 24, x: 0 },
  down: { y: -24, x: 0 },
  left: { x: 24, y: 0 },
  right: { x: -24, y: 0 },
};

export default function FadeIn({
  children,
  delay = 0,
  duration = 0.6,
  direction = 'up',
  once = true,
  amount = 0.2,
  as = 'div',
  className = '',
  ...rest
}) {
  const MotionTag = motion[as];
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, ...DIRECTION_OFFSETS[direction] }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount }}
      transition={{
        duration,
        delay,
        // Smooth ease-out cubic — matches the rhythm of the local Reveal
        // components this replaces.
        ease: [0.16, 1, 0.3, 1],
      }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
